/**
 * Atendimentos API client (PRD 4, Story 4.1.4).
 *
 * Wraps backend routes under ``/api/v1/atendimentos`` (painel
 * colaborador interno — NÃO confundir com ``/portal/atendimentos`` que é
 * o endpoint do parceiro autenticado).
 *
 * Backend ref: V1/backend/app/api/v1/endpoints/atendimentos.py
 * Backend service ref: V1/backend/app/services/customer_session_service.py
 */

import api from "@/services/api";
import type {
  ActivatePassthroughRequest,
  AvailableAgentsListResponse,
  CloseSessionRequest,
  CustomerSession,
  CustomerSessionExportResponse,
  CustomerSessionExportStatusResponse,
  CustomerSessionMessage,
  IniciarAtendimentoBody,
  ListSessionsParams,
  MessageListResponse,
  ParceirosParaContatoResponse,
  PartnerActiveSessionsCount,
  ScreenShareRequestApiResponse,
  ScreenShareRequestPayload,
  SendMessageRequest,
  SessionListResponse,
  TransferSessionRequest,
  WhatsAppPassthroughStatus,
} from "@/types/atendimento";

export const atendimentosApi = {
  listSessions: async (params?: ListSessionsParams): Promise<SessionListResponse> => {
    const r = await api.get<SessionListResponse>("/atendimentos", { params });
    return r.data;
  },

  getSession: async (id: string): Promise<CustomerSession> => {
    const r = await api.get<CustomerSession>(`/atendimentos/${id}`);
    return r.data;
  },

  /**
   * Atendimento ativo — busca as lojas (parceiros ativos) e as contas do portal delas para o "Novo atendimento".
   * `busca` é termo literal (nome, código ou razão social; vazio lista todas); `page_size` vai até 50.
   * Exige `atendimentos:create`.
   */
  buscarLojasParaContato: async (params: {
    busca: string;
    page: number;
    page_size: number;
  }): Promise<ParceirosParaContatoResponse> => {
    const r = await api.get<ParceirosParaContatoResponse>("/atendimentos/lojas-para-contato", {
      params,
    });
    return r.data;
  },

  /**
   * Atendimento ativo — o SE abre a conversa com uma conta do portal e grava a primeira mensagem (201, `origem: "SE"`).
   * 404 "Conta não encontrada." se a conta não pode receber; 422 se o cadastro de quem envia não tem setor.
   * Não há dedup: cada chamada abre uma conversa nova. Exige `atendimentos:create`.
   */
  iniciarAtendimento: async (body: IniciarAtendimentoBody): Promise<CustomerSession> => {
    const r = await api.post<CustomerSession>("/atendimentos/iniciar", body);
    return r.data;
  },

  // Paginação por cursor: `before_id` (mensagens mais antigas, scroll pra cima) ou
  // `after_id` (poll de novidades, só o que é mais novo). Sem cursor = as mais
  // recentes. `offset` mantido só p/ compat legada (não usado pelo chat paginado).
  listMessages: async (
    sessionId: string,
    params?: { limit?: number; before_id?: string; after_id?: string; offset?: number }
  ): Promise<MessageListResponse> => {
    const r = await api.get<MessageListResponse>(`/atendimentos/${sessionId}/messages`, { params });
    return r.data;
  },

  sendMessage: async (
    sessionId: string,
    body: SendMessageRequest
  ): Promise<CustomerSessionMessage> => {
    const r = await api.post<CustomerSessionMessage>(`/atendimentos/${sessionId}/messages`, body);
    return r.data;
  },

  closeSession: async (
    sessionId: string,
    body: CloseSessionRequest = {}
  ): Promise<CustomerSession> => {
    const r = await api.post<CustomerSession>(`/atendimentos/${sessionId}/close`, body);
    return r.data;
  },

  /** Assume (pega) a sessão da fila: QUEUED→IN_PROGRESS atribuída ao agente atual.
   *  409 se já foi assumida ou não está na fila. */
  takeSession: async (sessionId: string): Promise<CustomerSession> => {
    const r = await api.post<CustomerSession>(`/atendimentos/${sessionId}/take`);
    return r.data;
  },

  /**
   * Story E9.1.3 — transferência DIRETA (sem PENDING).
   *
   * XOR strict no body: `to_user_id` OU `to_setor_id` (exatamente um).
   * Sucesso retorna a sessão atualizada (novo assigned_user_id /
   * sector_id / status / transferred_count). 422 com `detail` textual
   * pros vários invariantes do service (max_transfers_reached,
   * self_transfer_noop, etc.); 404 anti-enumeration cross-tenant.
   */
  transferSession: async (
    sessionId: string,
    body: TransferSessionRequest
  ): Promise<CustomerSession> => {
    const r = await api.post<CustomerSession>(`/atendimentos/${sessionId}/transfer`, body);
    return r.data;
  },

  /**
   * Story E9.1.3 — agentes ONLINE no setor (para TransferDialog dropdown).
   *
   * Ordenado por carga (`open_count` ASC, `last_heartbeat_at` ASC).
   * Resposta `count == 0` = nenhum ONLINE → UI mostra estado vazio na
   * tab "Agente" e libera só a tab "Setor".
   */
  listAvailableAgents: async (sectorId: string): Promise<AvailableAgentsListResponse> => {
    const r = await api.get<AvailableAgentsListResponse>(
      `/atendimentos/sectors/${sectorId}/available-agents`
    );
    return r.data;
  },

  /**
   * Story 4.5.1 — conta sessões ativas do parceiro (AWAITING+IN_PROGRESS)
   * no tenant atual. Consumido pelo `PartnerSidebar` pra mostrar badge
   * de overlap quando `active_count >= 2`.
   */
  getPartnerActiveSessionsCount: async (partnerId: string): Promise<PartnerActiveSessionsCount> => {
    const r = await api.get<PartnerActiveSessionsCount>("/atendimentos/active-count", {
      params: { partner_id: partnerId },
    });
    return r.data;
  },

  /**
   * Story 4.5.2 — agente pede compartilhamento de tela do parceiro.
   *
   * Backend emite `call_data_event(event_type='screen_share_request',
   * sent_via='both')` — audit + dispatch via event_bus. O modal de aceite
   * chega no parceiro pelo data channel (PRD 3) ou por polling enquanto
   * PRD 3 não está ligado.
   *
   * 202 (Accepted). Erros típicos:
   * - 404: sessão inexistente / cross-tenant.
   * - 422 `session_not_active`: sessão QUEUED (sem agente) ou RESOLVED.
   * - 403: sem permissão `atendimentos:update`.
   */
  requestScreenShare: async (
    sessionId: string,
    body: ScreenShareRequestPayload = {}
  ): Promise<ScreenShareRequestApiResponse> => {
    const r = await api.post<ScreenShareRequestApiResponse>(
      `/atendimentos/${sessionId}/screen-share-request`,
      body
    );
    return r.data;
  },

  /**
   * Story 4.7.2 — status de WhatsApp passthrough do parceiro (ADR-413).
   * `is_whatsapp_only=true` → parceiro opera só via WhatsApp.
   */
  getPassthroughStatus: async (partnerId: string): Promise<WhatsAppPassthroughStatus> => {
    const r = await api.get<WhatsAppPassthroughStatus>(`/atendimentos/passthrough/${partnerId}`);
    return r.data;
  },

  /**
   * Story 4.7.2 — ativa o passthrough permanente do parceiro. Registra
   * quem ativou (do token) + `reason` opcional. 422 se já houver
   * subscription ativa ou se o ID não for de um parceiro do tenant.
   */
  activatePassthrough: async (
    partnerId: string,
    body: ActivatePassthroughRequest = {}
  ): Promise<WhatsAppPassthroughStatus> => {
    const r = await api.post<WhatsAppPassthroughStatus>(
      `/atendimentos/passthrough/${partnerId}`,
      body
    );
    return r.data;
  },

  /**
   * Story 4.7.2 — desativa o passthrough. Retorna o status pós-
   * desativação (`is_whatsapp_only=false`). 422 se não houver
   * subscription ativa.
   */
  deactivatePassthrough: async (partnerId: string): Promise<WhatsAppPassthroughStatus> => {
    const r = await api.delete<WhatsAppPassthroughStatus>(`/atendimentos/passthrough/${partnerId}`);
    return r.data;
  },

  /**
   * Story E9.2.4 — pede exportação ZIP histórico do atendimento.
   *
   * Backend cria row em `customer_session_exports` (status=PENDING) e
   * enfileira job ARQ `export_customer_session`. UI faz polling via
   * `getExportStatus` até status=READY (download_url presente).
   *
   * Permission backend: `atendimentos:export` (Admin + Manager).
   * Resposta: 202 Accepted com o row inicial PENDING.
   */
  requestExport: async (sessionId: string): Promise<CustomerSessionExportResponse> => {
    const r = await api.post<CustomerSessionExportResponse>(`/atendimentos/${sessionId}/export`);
    return r.data;
  },

  /**
   * Story E9.2.4 — polling do status do export.
   *
   * Retorna `download_url` quando status=READY e link não expirou (TTL 7d
   * a partir do upload original). UI deve abrir esse URL em nova janela
   * ou chamar download direto.
   */
  getExportStatus: async (
    sessionId: string,
    exportId: string
  ): Promise<CustomerSessionExportStatusResponse> => {
    const r = await api.get<CustomerSessionExportStatusResponse>(
      `/atendimentos/${sessionId}/exports/${exportId}`
    );
    return r.data;
  },
};
